import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import { catchError, map, Observable, of } from "rxjs";

import { RequestConfig, RequestState, RequestStatus, SettingsPost, SettingsResponse } from "@shared/models";
import { ApiCacheService } from "@shared/services/util/api-cache.service";
import { environment } from "@environments/environment";
import { CacheId, MessageType } from "@shared/enums";
import { ErrorHandlingService } from "@shared/services/util/error-handling.service";
import { GlobalService } from "@shared/services/environment/global.service";
import { MessageService } from "primeng/api";

@Injectable({
  providedIn: "root",
})
export class SettingsApiService {
  apiCacheService = inject(ApiCacheService);
  errorHandlingService = inject(ErrorHandlingService);
  messageService = inject(MessageService);
  globalService = inject(GlobalService);
  http = inject(HttpClient);

  private readonly _baseUrl = environment.imaxApiUrl;
  private _settingsRequestConfig: RequestConfig = {
    url: `${this._baseUrl}/GetSettings`,
    cacheId: CacheId.Settings,
    cacheValidMinutes: 15,
    forceRefresh: false,
  };

  getSettings(): Observable<RequestState<SettingsResponse>> {
    return this.apiCacheService.request<SettingsResponse>(this._settingsRequestConfig);
  }

  updateSettings(
    settings: SettingsResponse,
    vMaxDefault: string,
    projDefault: string
  ): Observable<RequestState<SettingsResponse>> {
    this.globalService.saving.set(true);
    settings.vMaxDefault = vMaxDefault;
    settings.projDefault = projDefault;
    const postBody: SettingsPost = {
      settingID: settings.settingID,
      vMaxDefault: vMaxDefault,
      projDefault: projDefault,
    };

    const handleResponse = (): RequestState<SettingsResponse> => {
      this.globalService.saving.set(false);
      return new RequestState<SettingsResponse>(RequestStatus.IsCompleteWithResponse, {
        ...settings,
        vMaxDefault,
        projDefault,
        timestamp: new Date().toISOString(),
      });
    };

    const handleError = (error: HttpErrorResponse): Observable<RequestState<SettingsResponse>> => {
      this.globalService.saving.set(false);
      if (error.status === 200) {
        localStorage.setItem(CacheId.Settings, JSON.stringify(settings));
        this.messageService.clear();
        this.messageService.add({
          severity: "success",
          summary: "Defaults Changed",
          detail: "Your defaults have been changed.",
          key: "noReply",
        });
        return of(new RequestState<SettingsResponse>(RequestStatus.IsCompleteWithResponse, settings));
      }
      if (error.status === 404) {
        this.errorHandlingService.sendMessage(MessageType.UpdateRecordNotFound);
        return of(new RequestState<SettingsResponse>(RequestStatus.HasErrorWithCachedResponse, settings));
      } else if (error.status === 500) {
        this.errorHandlingService.sendMessage(MessageType.ServerError);
        return of(new RequestState<SettingsResponse>(RequestStatus.HasErrorWithCachedResponse, settings));
      } else {
        this.errorHandlingService.sendMessage(MessageType.UnknownError);
        return of(new RequestState<SettingsResponse>(RequestStatus.HasErrorWithCachedResponse, settings));
      }
    };

    return this.http.post<string>(`${this._baseUrl}/SaveSettings`, postBody).pipe(
      map(() => handleResponse()),
      catchError((error: HttpErrorResponse) => handleError(error))
    );
  }
}
